import { Spin } from 'antd'
import { THUMBNAIL_FILE_PREFIX } from 'features/fde/rolodexAPI'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { ReactEventHandler, SyntheticEvent, useState } from 'react'
import styled from 'styled-components'

const CenteredSpin = styled(Spin)`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`

export const ThumbnailImage: React.FC<
  { src: string; key?: string } & React.ImgHTMLAttributes<HTMLImageElement>
> = ({ src = '', ...rest }) => {
  const [path] = src.split('?')
  const [entryID, filePath] = path.split('/').slice(-2)

  const { urls, isLoading, errors } = useRolodexSignedURLs({
    entry_id: entryID,
    path: `${THUMBNAIL_FILE_PREFIX}${filePath}`,
  })

  // If there's an error fetching the thumbnail (e.g., 404 when thumbnail doesn't exist),
  // gracefully fall back to the original src without propagating the error
  const hasError = errors.some((e) => e !== undefined)
  const thumbnailURL = hasError ? src : (urls[0] ?? src)
  const fallbackURLs = thumbnailURL !== src ? [src] : []

  return isLoading || !src ? (
    <CenteredSpin spinning={true} />
  ) : (
    <ImageWithFallbacks
      src={thumbnailURL}
      fallbackSrcs={fallbackURLs}
      {...rest}
    />
  )
}

const ImageWithFallbacks: React.FC<
  {
    fallbackSrcs: (string | undefined)[]
    src: string
    key?: string
    onError?: ReactEventHandler<HTMLImageElement>
  } & React.ImgHTMLAttributes<HTMLImageElement>
> = ({ src, fallbackSrcs, onError, ...rest }) => {
  const [currentSrc, setCurrentSrc] = useState<string>(src)
  const [srcIndex, setSrcIndex] = useState<number>(0)

  const cycleSources = (err: SyntheticEvent<HTMLImageElement>) => {
    setSrcIndex(srcIndex + 1)
    const newSrc = fallbackSrcs.at(srcIndex)
    if (newSrc) {
      setCurrentSrc(newSrc)
    } else {
      if (onError) onError(err)
    }
  }

  return <img src={currentSrc} onError={cycleSources} {...rest} />
}
